import { getUserProfile } from 'helpers/userProfile';
import { useFilterContext } from 'pages/NewOverview/contexts/FilterContext';
import { useHealthDataContext } from 'pages/NewOverview/contexts/HealthDataContext';
import { handleFilterStateRoute } from 'pages/NewOverview/helpers/filters';
import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { LoadingUnitsInfo } from '~/pages/NewOverview/components/Loading/UnitsInfo';
import { HealthUnitCard } from './Card';
import { HealthUnitsInfoContainer } from './styles';

interface HealthUnitsInfoProps {
  loading: boolean;
  hasHealth: boolean;
}

export function HealthUnitsInfo({
  hasHealth,
  loading,
}: Readonly<HealthUnitsInfoProps>): ReactElement {
  const [profile] = useState(getUserProfile());
  const history = useHistory();
  const { data, loading: loadingHealth, refetch } = useHealthDataContext();
  const { client, selectedOptions } = useFilterContext();

  const generalLoading = useMemo(() => loading || (loadingHealth && hasHealth), [hasHealth, loading, loadingHealth]);

  const hasPredictiveAccess = useMemo(() => {
    const ALLOWED_PREDICTIVE_CLIENT_IDS = new Set([145, 149]);

    return profile.permissions.PER_CLIENT.some(
      (item) => ALLOWED_PREDICTIVE_CLIENT_IDS.has(item.clientId),
    );
  }, [profile.permissions.PER_CLIENT]);

  function onClickDetails(health: number[]): void {
    if (hasPredictiveAccess) {
      history.push('/analise/preditivo');
    } else {
      history.push('/analise/maquinas?tipo=machines',
        handleFilterStateRoute(client, selectedOptions, profile, health));
    }
  }

  function onClickOfflineDetails(): void {
    history.push('/analise/conectividade',
      handleFilterStateRoute(client, selectedOptions, profile));
  }

  useEffect(() => {
    if (hasHealth && !loading) {
      refetch(Number(client), selectedOptions);
    }
  }, [
    client,
    selectedOptions.healthStatus,
    selectedOptions.group,
    selectedOptions.state,
    hasHealth,
    loading,
  ]);

  if (generalLoading) {
    return (<LoadingUnitsInfo />);
  }

  const HEALTHY_VALUE_HEALTH = 100;
  const ALERT_VALUE_HEALTH = 50;
  const CRITICAL_VALUE_HEALTH = 25;

  return (
    <HealthUnitsInfoContainer data-testId="health-units-info">
      <HealthUnitCard
        variant="healthy"
        onClickDetails={() => onClickDetails([HEALTHY_VALUE_HEALTH])}
        value={hasHealth && !loading ? data?.healthy : undefined}
      />
      <HealthUnitCard
        variant="alert"
        onClickDetails={() => onClickDetails([ALERT_VALUE_HEALTH])}
        value={hasHealth && !loading ? data?.alert : undefined}
      />
      <HealthUnitCard
        variant="critical"
        onClickDetails={() => onClickDetails([CRITICAL_VALUE_HEALTH])}
        value={hasHealth && !loading ? data?.critical : undefined}
      />
      <HealthUnitCard
        variant="inoperative"
        onClickDetails={onClickOfflineDetails}
        value={hasHealth && !loading ? data?.inoperative : undefined}
      />
    </HealthUnitsInfoContainer>
  );
}
